<template>
  <div
    ref="menu"
    class="gw_pop_menu"
    :class="{ gw_pop_menu_open: open }"
    @mouseleave="handleMouseLeave"
  >
    <slot
      name="before"
      :methods="{ toggleOpen }"
    ></slot>
    <div
      v-if="!referenceEl && !useTargetElement"
      class="gw_pop_menu_opener"
      v-bind="open ? { id: `${open}_opener` } : {}"
      @click="e => handleClickOpener(!open, e)"
      @mouseenter="handleMouseOpener(true)"
    >
      <slot
        name="opener"
        :value="open"
        :methods="{ toggleOpen }"
      ></slot>
    </div>

    <div
      v-if="isInside && (isEager || open)"
      v-show="open"
      :id="open"
      :key="open"
      style="position: var(--inside-menu-position); min-width: var(--inside-menu-min-width)"
      class="gw_pop_menu_content anim-fade-in text-noselect"
      @mousedown="mousedownInside = true"
    >
      <slot :methods="{ toggleOpen }"></slot>
    </div>

    <portal
      v-else-if="isEager ? true : open"
      to="main_before"
    >
      <div
        v-show="open"
        :id="open"
        :key="open"
        style="position: fixed"
        :style="`min-width: ${minWidth}px; z-index: ${zIndexContent}`"
        class="gw_pop_menu_content anim-fade-in text-noselect"
        @mouseleave="handleMouseLeave"
        @mousedown="mousedownInside = true"
      >
        <slot :methods="{ toggleOpen }"></slot>
      </div>
    </portal>
    <slot
      name="after"
      :value="open"
      :methods="{ toggleOpen }"
    ></slot>
  </div>
</template>

<script>
import float_mix from '@/mixins/float_mix'

export default {
  name: 'GwMenu',
  mixins: [float_mix],
  props: {
    isMovable: Boolean,
    ignoreBoundary: { type: Boolean, default: true },
    ignoreDataGwmenu: Boolean,
    useVirtualElement: Boolean,
    useTargetElement: Boolean,
    isEager: Boolean,
    referenceEl: [HTMLElement, Object],
    boundary: [HTMLElement, Object],
    placement: { default: 'bottom-start' },
    flOptions: { default: () => ({ offset: 4, padding: 0 }) },
    closeOnOutsideClick: { type: Boolean, default: true },
    isInside: Boolean,
    trigger: { default: 'click' },
    hasStretch: { default: true },
    zIndexContent: { default: 99 },
    isOpen: { type: Boolean, default: false },
  },
  emits: ['close', 'open'],
  data() {
    this.mousedownInside = false
    return {
      l_referenceEl: null,
      open: false,
      closeOnMouseup: false,
      minWidth: 0,

      offsetX: null,
      offsetY: null,
    }
  },
  beforeUnmount() {
    this.removeListeners()
  },
  methods: {
    setListeners() {
      document.addEventListener('mouseleave', this.closeOnWindowLeave)
      document.addEventListener('mousedown', this.handleMousedown)
      document.addEventListener('mouseup', this.handleMouseup)
      document.addEventListener('touchstart', this.handleMousedown)
      document.addEventListener('touchend', this.handleMouseup)
      if (this.isMovable) {
        document.addEventListener('mousemove', this.moveMenu, { passive: false })
      }
    },
    removeListeners() {
      document.removeEventListener('mouseleave', this.closeOnWindowLeave)
      document.removeEventListener('mousedown', this.handleMousedown)
      document.removeEventListener('mouseup', this.handleMouseup)
      document.removeEventListener('touchstart', this.handleMousedown)
      document.removeEventListener('touchend', this.handleMouseup)
      document.removeEventListener('mousemove', this.moveMenu)
    },
    moveMenu(e) {
      if (!this.mousedownInside) return
      if (this.closeOnMouseup) return

      e.preventDefault()
      const boundary = document.getElementById('gw_main')
      const tooltip = document.getElementById(this.open)
      if (!tooltip || !boundary) return

      if (this.offsetX === null && this.offsetY === null) {
        const { x, y } = tooltip.getBoundingClientRect()
        this.offsetX = x - e.clientX
        this.offsetY = y - e.clientY
      }

      this.fl_followMouse(
        e,
        boundary,
        tooltip,
        {
          offsetY: this.offsetY,
          offsetX: this.offsetX,
          padding: 0,
          strategy: 'absolute',
          ignoreBoundary: this.ignoreBoundary,
        },
        {
          has_flip: false,
          has_shift: false,
        },
      )
    },
    resetDragProps() {
      this.mousedownInside = false
      this.offsetX = null
      this.offsetY = null
    },
    close() {
      this.toggleOpen(false)
      this.handleClose()
    },
    handleClose() {
      this.closeOnMouseup = false
      this.l_referenceEl = null
      this.removeListeners()
      this.resetDragProps()
    },
    handleClickOpener(bool, e) {
      if (this.trigger === 'click') {
        this.toggleOpen(bool, e)
      }
    },
    handleMouseOpener(bool) {
      if (this.trigger === 'hover') {
        this.toggleOpen(bool)
      }
    },
    toggleOpen(bool = !this.open, e) {
      // TODO Vue3 open is used in key and id (deprecation ATTR_FALSE_VALUE)
      this.open = bool ? this.open || `${Date.now() + Math.random()}` : false
      if (!this.open) {
        this.handleClose()
        this.$emit('close')
        return
      }
      requestAnimationFrame(() => {
        const referenceEl =
          this.useVirtualElement && e
            ? this.fl_getVirtualEl({ e })
            : this.useTargetElement
              ? e
                ? e.target
                : this.l_referenceEl
              : this.referenceEl || document.getElementById(`${this.open}_opener`)

        const element = document.getElementById(this.open)
        if (!(referenceEl && element)) {
          this.handleClose()
          return
        }
        this.l_referenceEl = referenceEl
        this.minWidth = this.hasStretch ? referenceEl.offsetWidth : 0

        const options = {
          strategy: 'fixed',
          placement: this.placement,
          ...this.flOptions,
        }
        this.fl_setPositionStyle({
          referenceEl,
          element,
          options,
          boundary: this.boundary || document,
        })
        this.setListeners()
        this.$emit('open')
      })
    },
    handleMouseup() {
      this.resetDragProps()
      if (this.closeOnMouseup) {
        this.close()
      }
    },
    closeOnWindowLeave() {
      if (this.trigger === 'hover') {
        this.close()
      }
    },
    handleMouseLeave(e) {
      if (this.trigger === 'hover') {
        const el = e.relatedTarget || e.toElement // firefox
        if (el && !this.cursorInside(el)) {
          this.toggleOpen(false)
        }
      }
    },
    handleMousedown(e) {
      this.closeOnMouseup = !this.closeOnOutsideClick ? false : !this.cursorInside(e.target)
    },
    cursorInside(el) {
      if (
        el.id === this.open ||
        el.id === `${this.open}_opener` ||
        (el.getAttribute('data-gwmenu') === 'noclose' && !this.ignoreDataGwmenu)
      ) {
        return true
      }
      if (!el.parentElement) {
        return false
      }
      return this.cursorInside(el.parentElement)
    },
  },
  watch: {
    isOpen(bool) {
      this.toggleOpen(bool)
    },
  },
}
</script>
<style lang="scss" scoped>
.gw_pop_menu {
  position: relative;
  --opener-position: relative;
  // --opener-display: block;

  --opener-inset: auto;
  --opener-inset: auto;
  --opener-height: 100%;
  --opener-width: 100%;
  --content-z-index: 99;

  --inside-menu-position: fixed;
  --inside-menu-min-width: auto;
  // --opener-position: absolute;
  // --opener-inset: 0 auto auto 0;
  &_open {
    // to overwrite gw_hover
    opacity: 1;
  }
  &_opener {
    // display: var(--opener-display);
    position: var(--opener-position);
    inset: var(--opener-inset);
    height: var(--opener-height);
    width: var(--opener-width);
  }
  &_content {
    z-index: var(--content-z-index, 10);
  }
  &_back {
    z-index: 9;
    position: fixed;
    top: 0;
    left: 0;
    // background: rgba(0, 242, 255, 0.382);
    width: 100vw;
    height: 100vh;
  }
}
</style>
